<template>
  <div class="play">

    <div class="map-wrapper">
      <div class="map-container">
        <span v-show="currentStage !== 'result'" class="crosshair"></span>
        <l-map
          ref="lMap"
          class="mapView"
          v-bind:zoom="dropZoom"
          v-bind:center="dropCenter"
          v-bind:options="mapOptions"
          @update:zoom="zoomUpdate"
          @update:center="centerUpdate"
          @dragend="dragEnd">
          <l-tile-layer
            v-bind:url="url"
            v-bind:attribution="tileLayerAttribution"
          />
          <l-control-attribution position="bottomleft" />
          <l-control v-show="currentStage === 'drop'" position="topright" >
            <button
              class="button zoom-button"
              @click="handleMapZoomOutButton">
              🔎 ➖
            </button>
          </l-control>
          <l-control v-show="currentStage === 'solve'" position="topright" >
            <button
              class="button zoom-button"
              @click="handleMapZoomInButton">
              🔎 ➕
            </button>
          </l-control>
          <l-control v-show="currentStage === 'result'" position="topright" >
            <button
              class="button zoom-button"
              @click="handleMapZoomInButton">
              🔎 ➕
            </button>
            <button
              class="button zoom-button"
              @click="handleMapZoomOutButton">
              🔎 ➖
            </button>
          </l-control>
          <l-marker v-if="currentStage === 'result'"
            :lat-lng="dropCenter"
            :icon="dropMarkerIcon">
            <l-tooltip style="color: #AA0000">Hier haben wir dich abgeworfen</l-tooltip>
          </l-marker>
          <l-marker v-if="currentStage === 'result'"
            :lat-lng="solveCenter"
            :icon="solveMarkerIcon">
            <l-tooltip style="color: #008000">Hier hast hast du hingezoomt</l-tooltip>
          </l-marker>
          <l-polyline v-if="currentStage === 'result'"
            :lat-lngs="centersAsArray"
            v-bind="distancePolylineOptions">
          </l-polyline>
          <l-rectangle v-if="currentStage === 'result'"
            :bounds="dropBoundsAsArray"
            v-bind="dropBoundsOptions">
          </l-rectangle>
          <l-rectangle v-if="currentStage === 'result'"
            :bounds="dropUserBoundsAsArray"
            v-bind="dropUserBoundsOptions">
          </l-rectangle>
          <l-rectangle v-if="currentStage === 'result'"
            :bounds="solveBoundsAsArray"
            v-bind="solveBoundsOptions">
          </l-rectangle>
        </l-map>
      </div>
      <button
        v-if="currentStage == 'drop'"
        @click="handleGoButton"
        class="button next-button">
        LOS GEHT'S 🚀
      </button>
      <button
        v-else-if="currentStage == 'solve'"
        @click="handleSolveButton"
        class="button next-button">
        LÖSEN ✔️
      </button>
      <button
        v-else-if="currentStage == 'result'"
        @click="handlePlayAgainButton"
        class="button next-button">
        NOCHMAL 🔄
      </button>
    </div>

    <div class="info-wrapper">
      <table class="info-table">
        <colgroup>
          <col class="col-description">
          <col class="col-value">
        </colgroup>
        <thead>
          <tr>
            <th colspan="2">{{ stage.message }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="info in stage.infos" v-bind:key="info.id">
            <td>{{ info.description }}</td>
            <td>{{ info.value }}</td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
/**
 * Play component. Consists of three play stages: drop, solve and result.
 * Maintains a single instance of a Leaflet map while modifying it and collecting
 * data (map center coordinates, zoom levels...)
 */
import {
  LMap,
  LTileLayer,
  LControl,
  LControlAttribution,
  LMarker,
  LPolyline,
  LRectangle,
  LTooltip
} from 'vue2-leaflet'
import { latLng, divIcon, latLngBounds } from 'leaflet'
import geo from '@/utils/geo'

export default {
  components: {
    LMap,
    LTileLayer,
    LControl,
    LControlAttribution,
    LMarker,
    LPolyline,
    LRectangle,
    LTooltip
  },
  props: {
    level: {
      type: [String, Number],
      default: 2
    }
  },
  data () {
    return {
      map: null, // for access to Leaflet API

      currentStage: 'drop',
      currentCenter: null,
      currentZoom: null,

      dropCenter: geo.randomCenter(),
      dropZoom: this.$config.zoomLevels[parseInt(this.level) - 1],
      dropUserZoom: null,
      dropBounds: null,
      dropUserBounds: null,

      solveCenter: null,
      solveZoom: null,
      solveBounds: null,

      allBounds: null,

      url:
        'https://server.arcgisonline.com' +
        '/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      tileLayerAttribution:
        'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, ' +
        'Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      mapOptions: {
        attributionControl: false,
        boxZoom: false,
        doubleClickZoom: false,
        dragging: false,
        keyboard: false,
        scrollWheelZoom: false,
        zoomControl: false,
        wheelPxPerZoomLevel: 100,
        zoomSnap: 1,
        maxZoom: 17,
        touchZoom: false
      },
      dropMarkerIcon: divIcon({
        className: 'dropMarker',
        iconSize: [70, 70],
        iconAnchor: [35, 70]
      }),
      solveMarkerIcon: divIcon({
        className: 'solveMarker',
        iconSize: [70, 70],
        iconAnchor: [35, 70]
      }),
      distancePolylineOptions: {
        color: 'white',
        weight: 5,
        opacity: 0.5
      },
      dropBoundsOptions: {
        color: '#ff5353',
        opacity: 0.5,
        fillColor: '#ff5353',
        fillOpacity: 0.1,
        weight: 2,
        dashArray: '4, 5'
      },
      dropUserBoundsOptions: {
        color: '#aa0000',
        opacity: 0.8,
        fillColor: '#aa0000',
        fillOpacity: 0.2,
        weight: 3
      },
      solveBoundsOptions: {
        color: '#008000',
        opacity: 0.8,
        fillColor: '#008000',
        fillOpacity: 0.2,
        weight: 3
      }
    }
  },
  computed: {
    dropBoundsAsArray () {
      return geo.boundsToVueLeafletArray(this.dropBounds)
    },
    dropUserBoundsAsArray () {
      return geo.boundsToVueLeafletArray(this.dropUserBounds)
    },
    solveBoundsAsArray () {
      return geo.boundsToVueLeafletArray(this.solveBounds)
    },
    distanceInKm () {
      return this.map.distance(this.dropCenter, this.solveCenter) / 1000
    },
    centersAsArray () {
      return [this.dropCenter, this.solveCenter]
    },
    stage () {
      switch (this.currentStage) {
        case 'drop':
          return {
            message: 'Abwurfkarte: Überlege, wo du bist. Du kannst zur Hilfe etwas rauszoomen.',
            infos: [{
              id: 'drop1',
              description: 'Abwurf-Zoom-Level:',
              value: this.dropZoom
            }, {
              id: 'drop2',
              description: 'Dein aktueller Zoom-Level:',
              value: this.currentZoom
            }]
          }
        case 'solve':
          return {
            message: 'Suchkarte: Verschiebe und zoome die Karte an den Ort, wo wir dich abgeworfen haben!',
            infos: [{
              id: 'solve1',
              description: 'Ziel-Zoom-Level aus letztem Schritt:',
              value: this.dropUserZoom
            }, {
              id: 'solve2',
              description: 'Dein aktueller Zoom-Level:',
              value: this.currentZoom
            }]
          }
        case 'result':
          return {
            message: 'Auflösungskarte',
            infos: [{
              id: 'result1',
              description: 'Entfernung in Kilometern:',
              value: Number
                .parseFloat(this.distanceInKm || 0)
                .toFixed(1)
                .replace('.', ',')
            }]
          }
        default:
          console.error('Error: Unknown stage name!')
      }
      return {}
    }
  },
  methods: {
    /**
     * Set up transition to second stage (solve map)
     */
    handleGoButton () {
      this.currentStage = 'solve'
      this.map.setView(latLng(0, 0), 2)
      this.map.dragging.enable()

      this.$gtag.event('play-go', {
        event_category: 'play-level-' + this.level
      })
    },
    /**
     * Set up transition to third stage (result map)
     */
    handleSolveButton () {
      this.currentStage = 'result'
      this.map.scrollWheelZoom.enable()
      this.map.keyboard.enable()
      this.map.touchZoom.enable()
      this.map.fitBounds(
        this.allBounds,
        {
          // FIXME: these option only apply from time to time
          // in a yet unreproducable manner
          padding: [3, 3],
          animate: true,
          duration: 1.3,
          easeLinearity: 0.25
        }
      )
      this.$gtag.event('play-solve', {
        event_category: 'play-level-' + this.level
      })
    },
    handlePlayAgainButton () {
      this.$router.push({ name: 'start' })
      this.$gtag.event('play-again', {
        event_category: 'play-level-' + this.level
      })
    },
    dragEnd (event) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`map dragged for ${event.distance.toFixed(0)} pixels`)
      }
      this.$gtag.event('map-drag', {
        event_category: 'play-level-' + this.level,
        event_label: this.currentStage + '-map',
        value: event.distance.toFixed(0)
      })
    },
    zoomUpdate (zoom) {
      this.currentZoom = zoom

      if (this.currentStage === 'drop') {
        this.dropUserZoom = zoom
        this.dropUserBounds = this.getCurrentBounds()
      }
      if (this.currentStage === 'solve') {
        this.solveZoom = zoom
        this.solveBounds = this.getCurrentBounds()
      }

      const allCornerLatLngs = [
        this.dropBounds.getNorthEast(),
        this.dropBounds.getSouthEast(),
        this.dropBounds.getSouthWest(),
        this.dropBounds.getNorthWest(),
        this.dropUserBounds.getNorthEast(),
        this.dropUserBounds.getSouthEast(),
        this.dropUserBounds.getSouthWest(),
        this.dropUserBounds.getNorthWest()
      ]
      if (this.solveBounds) {
        allCornerLatLngs.push([
          this.solveBounds.getNorthEast(),
          this.solveBounds.getSouthEast(),
          this.solveBounds.getSouthWest(),
          this.solveBounds.getNorthWest()
        ])
      }
      this.allBounds = latLngBounds(allCornerLatLngs)

      if (process.env.NODE_ENV === 'development') {
        console.log('currentZoom:', zoom)
      }
    },
    centerUpdate (center) {
      this.currentCenter = center

      if (this.currentStage === 'solve') {
        this.solveCenter = center
        this.solveBounds = this.getCurrentBounds()
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('currentCenter:', center.lat, center.lng)
      }
    },
    handleMapZoomOutButton () {
      this.map.zoomOut(1)

      this.$gtag.event('map-zoom-out', {
        event_category: 'play-level-' + this.level,
        event_label: this.currentStage + '-map'
      })
    },
    handleMapZoomInButton () {
      this.map.zoomIn(1)

      this.$gtag.event('map-zoom-in', {
        event_category: 'play-level-' + this.level,
        event_label: this.currentStage + '-map'
      })
    },
    getCurrentBounds () {
      return this.map.getBounds()
    }
  },
  async mounted () {
    // Fetch reference to Leaflet API
    await this.$nextTick(() => {
      this.map = this.$refs.lMap.mapObject
    })
    this.currentCenter = this.dropCenter
    this.currentZoom = this.dropZoom
    this.dropUserZoom = this.dropZoom
    this.dropBounds = this.getCurrentBounds()
    this.dropUserBounds = this.getCurrentBounds()
  }
}
</script>

<style lang="postcss">
@import url('//cdn.jsdelivr.net/font-zero-width/1.0.0/zero-width.css');

.play {
  display: flex;
  margin: 0 1.5rem 1.5rem 1.5rem;
  flex-direction: column;

  & .button {
    font-size: 1rem;
    padding: 0.8rem;
    background-color: white;
    outline: none;
    border-radius: .5rem;
    cursor: pointer;
  }

  & .map-wrapper {
    position: relative;

    & .map-container {
      box-shadow: 2px 2px 5px 0px rgba(0,0,0,0.3);
      & .crosshair {
        background-image: url('~@/assets/crosshairs_30.png');
        background-repeat: no-repeat;
        width: 30px;
        height: 30px;
        position: absolute;
        top: calc(50% - 30px / 2);
        left: calc(50% - 30px / 2);
        z-index: 450; /* leaflet overlayPane pane is at 400 */
        pointer-events: none;
        filter: var(--dropShadow);
      }

      & .solveMarker {
        filter: var(--dropShadow);
        background-image: url('~@/assets/solve-marker.svg');
        background-repeat: no-repeat;
      }

      & .dropMarker {
        filter: var(--dropShadow);
        background-image: url('~@/assets/drop-marker.svg');
        background-repeat: no-repeat;
      }

      & .mapView {
        & .zoom-button {
          margin-left: .5rem;
        }
      }

      & .leaflet-control-attribution {
        font-size: 0.8em;
        opacity: 0.7;
      }
    }

    & .next-button {
      position: absolute;
      z-index: 1000;
      bottom: -6px;
      right: -10px;
      border-radius: 2rem;
      padding: 0.8rem 1.1rem 0.8rem 1.1rem;
      background-color: var(--main-color);
      font-weight: bold;
    }
  }

  & .info-wrapper {
    flex-grow: 1;
  }

  & .info-table {
    margin-left: 0.4rem;
    width: 100%;
    border-spacing: 0.5em;
    font-family: var(--default-font-family);
    font-size: initial;

    & th {
      padding-bottom: 0.4rem;
      line-height: 1.6em;
    }

    & th, & td {
      vertical-align: bottom;
      border-collapse: collapse;
      text-align: left;
    }

    & tbody td:nth-child(2) {
      text-align: right;
      padding-left: 1em;
    }
  }
}

/* Layout switches aspect-ratio techniques for map-wrapper at AR of 10/8
   portrait: flex: column; and padding technique
   landscape: flex: row; and font-size: vh technique */

/* stacked layout (mainly portrait) with map at max width
   up until aspect ratio of 10/8 (slightly landscapey) */
@media (max-width: 1023px) and (max-aspect-ratio: 10/8) {
  .play {
    flex-direction: column;
    & .map-wrapper {
      height: 0;
      padding-top: 100%;
      margin-bottom: 20px;
      & .map-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
}

/* stacked layout (around 1/1 aspect rations) with map at 70% width to give
   space for infos below
   Extends portraitey media query */
@media (max-width: 1023px) and (min-aspect-ratio: 9/10) and (max-aspect-ratio: 10/8) {
  .play {
    width: 70%;
    margin-bottom: 1.5rem;
    align-self: center;
  }
}

/* row layout (from 10/8) with map at 85% height to give space for header */
@media (max-width: 1023px) and (min-aspect-ratio: 10/8) {
  .play {
    flex-direction: row;
    font-family: 'zero-width';
    font-size: 85vh;
    height: 1em;
    & .map-wrapper {
      flex-shrink: 0;
      /* Creates an 1:1 ar from parent's height, i.e. font-size */
      width: 1em;
      & .map-container {
        position: relative;
        width: 100%;
        height: 100%;
      }
    }
  }
}

/* stacked layout for large (desktop) screens with map at 80% height to let
   infos below not disappear too far in scroll area */
@media (min-width: 1024px) {
  .play {
    flex-direction: column;
    margin-bottom: 1.5rem;
    align-self: center;
    width: min-content;

    & .map-wrapper {
      font-family: 'zero-width';
      font-size: 65vh;
      height: 1em;
      margin-bottom: 20px;
      & .map-container {
        position: relative;
         /* Creates an 1:1 ar from parent's height, i.e. font-size */
        width: 1em;
        margin-bottom: 20px;
        height: 100%;
      }
    }
  }
}
</style>
